import { z } from 'zod';
// Monthly Plan (s215). Spec §6 — first Monday of month, "The Plan":
//   - founder-driven 30-min async (audio or text)
//   - monthly calendar: launches, seasonal events, tests
//   - budget review
//
// Unlike the other §6 cadences (recap/brief/drops are mag-initiated
// monologues), the Plan is bidirectional: Mag posts a prompt, the
// founder types their plan thinking, Mag synthesizes it into
// structured form. The card lives in two states — the same row
// transitions from 'awaiting_input' to 'synthesized' once the
// founder submits, so the conversation lineage is preserved in
// one place rather than split across two cards.
//
// Audio input is deferred to a follow-up session — needs a
// transcription path. The schema is text-only today; the future
// audio path just lands the transcript in `founderInput` and the
// rest works.
/** Mag's plan for one launch this month. Light scaffolding —
 *  details land in subsequent creative drops + concept proposals;
 *  this is just the founder-set anchor (what + when + why-now). */
export const planLaunchSchema = z.object({
    /** Short title — "BFCM teaser hook", "new SKU drop", lowercase. */
    title: z.string().min(1).max(80),
    /** When in the month — "early november", "week of nov 24",
     *  "before thanksgiving". Free-form because founders think in
     *  business rhythms not calendar dates. */
    whenIsh: z.string().min(1).max(60),
    /** One short sentence — why this launch, why this timing. */
    why: z.string().min(1).max(240),
});
/** A seasonal/external event Mag should plan around — holiday,
 *  industry trade show, founder's product event, etc. Not a Mag
 *  launch, just a date she should respect. */
export const planEventSchema = z.object({
    title: z.string().min(1).max(80),
    whenIsh: z.string().min(1).max(60),
    /** What it means for ad strategy ("expect 2x cpm on cold
     *  audiences", "shift to retargeting that week"). */
    implication: z.string().min(1).max(240),
});
/** A test Mag will run this month. Hypothesis-first, like creative
 *  drop concepts but at the strategic level (test new audience,
 *  test a new platform, test a new creative format). */
export const planTestSchema = z.object({
    title: z.string().min(1).max(80),
    hypothesis: z.string().min(1).max(240),
    /** What success looks like — the falsifiable observation. */
    successLooksLike: z.string().min(1).max(240),
});
/** A single line in the budget review — channel/campaign + dollar
 *  intent + brief rationale. Total is on the parent. */
export const planBudgetAllocationSchema = z.object({
    /** Channel or campaign label — "meta · cold prospecting",
     *  "google search · branded", "tiktok · ugc test". */
    label: z.string().min(1).max(80),
    /** Pre-formatted display value the dashboard renders verbatim
     *  ("$3,500", "40% of total"). Pre-formatting in the agent
     *  means the UI doesn't carry currency logic per allocation. */
    amountDisplay: z.string().min(1).max(40),
    /** Short rationale — "doubling down because the testimonial
     *  hook is still scaling", "scaling back, ctr decayed". */
    rationale: z.string().min(1).max(240),
});
export const planBudgetReviewSchema = z.object({
    /** Pre-formatted total ("$10,000", "~$8.5k", "no change from
     *  last month"). Free-form so founders' "ish" budgets render
     *  faithfully. */
    totalDisplay: z.string().min(1).max(60),
    /** What changed vs last month — one short paragraph. Null when
     *  there's no prior month to compare to. */
    changeVsLastMonth: z.string().min(1).max(400).nullable(),
    /** 1..6 allocation rows. */
    allocation: z.array(planBudgetAllocationSchema).min(1).max(6),
});
/** Awaiting-input state: Mag's prompted, no founder text yet. */
export const monthlyPlanAwaitingPayloadSchema = z.object({
    kind: z.literal('monthly_plan'),
    state: z.literal('awaiting_input'),
    /** ISO YYYY-MM string of the month this plan covers (computed
     *  in brand tz). Per-month dedup key. */
    planMonth: z.string(),
    tz: z.string().min(1).max(60),
    /** Mag's prompt to the founder — what she wants them to share.
     *  Mag voice, conversational. */
    prompt: z.string().min(1).max(500),
});
/** Synthesized state: founder submitted text, Mag turned it into
 *  the structured plan. */
export const monthlyPlanSynthesizedPayloadSchema = z.object({
    kind: z.literal('monthly_plan'),
    state: z.literal('synthesized'),
    planMonth: z.string(),
    tz: z.string().min(1).max(60),
    /** The founder's raw text input, kept verbatim. The card renders
     *  it as the seed above the synthesis so the lineage stays
     *  visible — Mag's reading of their words, with their words
     *  still attached. */
    founderInput: z.string().min(1).max(8000),
    /** When the founder submitted. ISO timestamp. */
    submittedAt: z.string(),
    launches: z.array(planLaunchSchema).max(8),
    events: z.array(planEventSchema).max(6),
    tests: z.array(planTestSchema).max(4),
    budgetReview: planBudgetReviewSchema,
    /** One paragraph from Mag closing the plan — what she's
     *  watching for, what would make her flag for an off-cycle
     *  check-in, one promise she's making. */
    magClosingNote: z.string().min(1).max(500),
});
/** Either state. The dashboard branches on `state`. */
export const monthlyPlanPayloadSchema = z.discriminatedUnion('state', [
    monthlyPlanAwaitingPayloadSchema,
    monthlyPlanSynthesizedPayloadSchema,
]);
/** What the synthesis agent returns. submittedAt + founderInput +
 *  planMonth + tz are stamped by the server, not the LLM. */
export const monthlyPlanAgentOutputSchema = z.object({
    launches: z.array(planLaunchSchema).max(8),
    events: z.array(planEventSchema).max(6),
    tests: z.array(planTestSchema).max(4),
    budgetReview: planBudgetReviewSchema,
    magClosingNote: z.string().min(1).max(500),
});
//# sourceMappingURL=monthly-plan.js.map